import React, { FC, useCallback, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/table';
import { columns, DEFAULT_FILTERS, filterConfig } from './constants';
import { useDeleteUserMutation, useGetUserManagementUsersQuery } from './api/userManagementApi';
import TableFilters from './components/table-filters';
import { User, UserStatus } from './api/types';
import UserManagementDialog from './components/user-management-dialog';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ActionsColumn from '../../components/actions';
import { Column, StickyPosition } from '../../components/table/types';
import PageWrapper from '../../components/page-wrapper';
import { useTableFilters, useQueryFilters } from '../../components/table/hooks';
import { useConfigurableColumns } from '../../components/table/hooks/useColumnManagement';
import { useGetProfileQuery } from '../../features/profile/api/profileApi';
import { useDispatch } from 'react-redux';
import { addNotification } from '../notification/slices/notificationSlice';
import { NotificationType } from '../notification/types';
import ConfirmationDialog from '../../components/confirmation';
import { useGetTenantProductsQuery } from '../products/api/productsApi';

const UserManagement: FC = () => {
    const dispatch = useDispatch();
    const { data: user, isLoading: isUserLoading } = useGetProfileQuery();
    const { data: products } = useGetTenantProductsQuery();
    const [deleteUser] = useDeleteUserMutation();
    const [processing, setProcessing] = useState(false);

    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const { filters, resetFilters, updateFilters, setPage, setRowsPerPage, setSorted } = useTableFilters({
        defaultFilters: DEFAULT_FILTERS,
    });

    const queryFilters = useQueryFilters({ ...filters, email: user?.email }, filterConfig);
    const {
        data,
        isLoading: isTableLoading,
        refetch,
    } = useGetUserManagementUsersQuery(queryFilters, {
        skip: isUserLoading || !user?.email,
    });

    const totalCount = data?.totalCount || 0;
    const userManagementData = data?.value || [];

    const handleUserManagementDialog = useCallback((user?: User) => {
        setSelectedUser(user || null);
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(
        (redirectToFirstPage?: boolean) => {
            setDialogOpen(false);
            if (redirectToFirstPage) {
                updateFilters('pageNumber', 0);
            }
            setSelectedUser(null);
        },
        [updateFilters]
    );

    const handleDelete = useCallback((user?: User) => {
        setSelectedUser(user || null);
        setConfirmationDialog(true);
    }, []);

    const onDelete = async () => {
        if (!selectedUser || processing) return;
        try {
            setProcessing(true);
            await deleteUser({ id: selectedUser.id }).unwrap();
            dispatch(addNotification({ message: t('user-deleted'), type: NotificationType.SUCCESS }));
            setConfirmationDialog(false);
        } catch (error) {
            dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
        } finally {
            setProcessing(false);
        }
    };

    const actionsColumn: Column = useMemo(
        () => ({
            id: 'actions',
            title: 'Actions',
            component: (props) => {
                const action =
                    props.status === UserStatus.ACTIVE
                        ? { icon: ModeEditOutlineOutlinedIcon, tooltip: 'Edit', onClick: handleUserManagementDialog }
                        : { icon: DeleteOutlineOutlinedIcon, tooltip: 'Delete', onClick: handleDelete };

                return <ActionsColumn {...props} actions={[action]} />;
            },
            sticky: StickyPosition.RIGHT,
            width: 120,
            alignHeader: 'center',
        }),
        [handleUserManagementDialog, handleDelete]
    );

    const allColumns: Array<Column> = useMemo(() => [...columns, actionsColumn], [actionsColumn]);
    const { displayedColumns } = useConfigurableColumns({ columns: allColumns });

    return (
        <PageWrapper title={t('user-management')}>
            <Paper sx={{ mt: 4, pt: 1 }}>
                <TableFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    mainAction={() => handleUserManagementDialog()}
                    refetchData={refetch}
                />
                <DataTable
                    data={userManagementData}
                    columns={displayedColumns}
                    page={filters.pageNumber}
                    setPage={setPage}
                    rowsPerPage={filters.pageSize}
                    setRowsPerPage={setRowsPerPage}
                    total={totalCount}
                    dataLoading={isTableLoading || isUserLoading}
                    resetFilters={resetFilters}
                    setSorted={setSorted}
                />
            </Paper>
            {dialogOpen && products && (
                <UserManagementDialog
                    visible={dialogOpen}
                    onClose={handleCloseDialog}
                    user={selectedUser}
                    products={products}
                />
            )}
            <ConfirmationDialog
                isOpen={confirmationDialog}
                onClose={() => setConfirmationDialog(false)}
                onConfirm={onDelete}
                description={t('delete-user-info')}
            />
        </PageWrapper>
    );
};

export default UserManagement;
